import { useState, useEffect } from 'react';
import { X, Loader2, ArrowDownCircle, ArrowUpCircle, History } from 'lucide-react';
import api from '../api/api';

const TYPE_LABELS = { in: 'Entrada', out: 'Salida', adjust: 'Ajuste' };

export default function MovementModal({ item, onClose, onSaved }) {
  const [type, setType] = useState('out');
  const [quantity, setQuantity] = useState(1);
  const [reason, setReason] = useState('');
  const [woCode, setWoCode] = useState('');
  const [movements, setMovements] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const loadMovements = () => {
    api.get(`/inventory/${item.id}/movements`).then(r => setMovements(r.data)).catch(() => setMovements([]));
  };

  useEffect(() => { loadMovements(); }, [item.id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      await api.post(`/inventory/${item.id}/movement`, {
        type,
        quantity: parseFloat(quantity) || 0,
        reason,
        work_order_code: woCode,
      });
      setQuantity(1); setReason(''); setWoCode('');
      loadMovements();
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al registrar el movimiento');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="movement-modal">
        <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white">
          <div className="min-w-0">
            <h2 className="text-base font-bold text-gray-900 truncate" style={{ fontFamily: 'Barlow, sans-serif' }}>
              Movimiento de stock
            </h2>
            <p className="text-xs text-gray-500 truncate">{item.code} · {item.name}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1" data-testid="close-movement-modal"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-5">
          <div className="flex items-baseline gap-2 p-3 bg-gray-50 rounded-lg">
            <span className="text-xs text-gray-500">Stock actual:</span>
            <span className="font-mono font-bold text-lg text-gray-900" data-testid="movement-current-stock">{item.quantity}</span>
            <span className="text-xs text-gray-500">{item.unit}</span>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm" data-testid="movement-error">{error}</div>}

            <div className="grid grid-cols-3 gap-2">
              {[['out', 'Salida', ArrowUpCircle], ['in', 'Entrada', ArrowDownCircle], ['adjust', 'Ajuste', History]].map(([val, label, Icon]) => (
                <button
                  key={val} type="button" onClick={() => setType(val)}
                  className={`flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border transition-colors ${type === val ? 'bg-[#16a34a] text-white border-[#16a34a]' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                  data-testid={`movement-type-${val}`}
                >
                  <Icon size={14} /> {label}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {type === 'adjust' ? 'Stock real contado' : 'Cantidad'}
                </label>
                <input
                  type="number" step="0.01" min="0" value={quantity} required
                  onChange={e => setQuantity(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none font-mono"
                  data-testid="movement-quantity"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Nº de OT (opcional)</label>
                <input
                  type="text" value={woCode}
                  onChange={e => setWoCode(e.target.value)}
                  placeholder="OT-2026-0012"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none"
                  data-testid="movement-wo"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Motivo</label>
              <input
                type="text" value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder={type === 'out' ? 'Ej: Cambio de filtro en pala cargadora' : 'Ej: Recepción de pedido'}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none"
                data-testid="movement-reason"
              />
            </div>

            <button type="submit" disabled={saving}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium bg-[#16a34a] text-white rounded-lg hover:bg-[#15803d] disabled:opacity-50"
              data-testid="save-movement-btn">
              {saving && <Loader2 size={14} className="animate-spin" />}
              {saving ? 'Registrando...' : `Registrar ${TYPE_LABELS[type].toLowerCase()}`}
            </button>
          </form>

          <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <History size={13} /> Historial ({movements.length})
            </h3>
            {movements.length === 0 ? (
              <p className="text-xs text-gray-400 py-3" data-testid="no-movements">Sin movimientos registrados todavía.</p>
            ) : (
              <div className="space-y-1.5 max-h-56 overflow-y-auto" data-testid="movements-list">
                {movements.map(m => (
                  <div key={m.id} className="flex items-start gap-2 text-xs border border-gray-100 rounded-lg px-3 py-2" data-testid={`movement-${m.id}`}>
                    <span className={`font-semibold flex-shrink-0 ${m.type === 'in' ? 'text-green-600' : m.type === 'out' ? 'text-red-600' : 'text-gray-500'}`}>
                      {m.type === 'in' ? `+${m.quantity}` : m.type === 'out' ? `−${m.quantity}` : `=${m.resulting_quantity}`}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-gray-700 truncate">
                        {TYPE_LABELS[m.type]} · queda {m.resulting_quantity} {item.unit}
                        {m.work_order_code && <span className="font-mono text-gray-500"> · {m.work_order_code}</span>}
                      </p>
                      {m.reason && <p className="text-gray-500 truncate">{m.reason}</p>}
                      <p className="text-gray-400">
                        {new Date(m.timestamp).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })} · {m.by_user}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
